import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { createRoute, createTrip, updateTrip, type Trip } from './api';
import { parseKm } from './calc';
import { missingTripFields, toTripBody, type TripField, type TripForm } from './trip-form';

const KNOWN = [
  'MILEAGE_OFF',
  'NO_RATE',
  'NOT_YOUR_VEHICLE',
  'VEHICLE_ARCHIVED',
  'MONTH_CLOSED',
  'REPORT_NOT_AVAILABLE',
  'EXPENSE_OUT_OF_PERIOD',
  'VERSION_CONFLICT',
] as const;
export type TripProblem = (typeof KNOWN)[number] | 'OTHER';

/** The problem to show in words: the code when the page names it, else `OTHER`. */
function problemKey(code: string): TripProblem {
  return (KNOWN as readonly string[]).includes(code) ? (code as TripProblem) : 'OTHER';
}

/**
 * Saving a trip: the fields that block it show after the first try; the server's problem comes
 * back by code. A new trip may also be kept as a «Percorso usuale» (best effort, after the trip is
 * stored), then the expense opens. A duplicate warning is told in a toast.
 */
export function useTripSave(editing: Trip | null) {
  const { t } = useTranslation('expenses');
  const navigate = useNavigate();
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<TripProblem | null>(null);

  const invalidFor = (form: TripForm): ReadonlySet<TripField> =>
    tried ? missingTripFields(form) : new Set();

  const save = async (form: TripForm): Promise<void> => {
    setTried(true);
    if (busy || missingTripFields(form).size > 0) return;
    setBusy(true);
    setProblem(null);
    const body = toTripBody(form);
    const outcome = editing
      ? await updateTrip(editing.id, editing.version, body)
      : await createTrip(body);
    if (!outcome.ok) {
      setBusy(false);
      setProblem(problemKey(outcome.code));
      return;
    }
    if (form.saveRoute && !editing) {
      await createRoute({ from: body.from, to: body.to, km: parseKm(form.km) as string });
    }
    if (outcome.value.warnings.some((w) => w.code === 'DUPLICATE_TRIP')) {
      toast.warning(t('mileage.trip.duplicate'));
    }
    void navigate(`/expenses/${encodeURIComponent(outcome.value.id)}`);
  };

  return { busy, problem, invalidFor, save };
}
